export const APP_LOCAL_STORAGE_OWNER_KEY = "upsearch-local-storage-owner";

const TRACKED_LOCAL_STORAGE_KEYS = new Set([
  APP_LOCAL_STORAGE_OWNER_KEY,
  "currentSearchSpecificationId",
  "user-preferences-store",
]);

const TRACKED_LOCAL_STORAGE_PREFIXES = [
  "brand-store-",
  "search-specification-store-",
  "user-preferences-store-",
];

type LocalStorageOwnerRecord = {
  userId: string;
  verifiedAt: string;
  version: 1;
};

export type LocalStorageOwnershipSyncResult =
  | "skipped"
  | "verified"
  | "cleared-unowned"
  | "cleared-other-user";

const isBrowser = () => typeof window !== "undefined";

const getLocalStorageKeys = (): string[] => {
  if (!isBrowser()) {
    return [];
  }

  try {
    return Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter(
      (key): key is string => Boolean(key),
    );
  } catch (error) {
    console.error("Failed to enumerate localStorage keys:", error);
    return [];
  }
};

const isTrackedLocalStorageKey = (key: string): boolean =>
  TRACKED_LOCAL_STORAGE_KEYS.has(key) ||
  TRACKED_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));

const readOwnerRecord = (): LocalStorageOwnerRecord | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = localStorage.getItem(APP_LOCAL_STORAGE_OWNER_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<LocalStorageOwnerRecord>;
    if (typeof parsed.userId !== "string" || parsed.userId.trim() === "") {
      return null;
    }

    return {
      userId: parsed.userId,
      verifiedAt: typeof parsed.verifiedAt === "string" ? parsed.verifiedAt : "",
      version: 1,
    };
  } catch (error) {
    console.error("Failed to parse localStorage owner record:", error);
    return null;
  }
};

const writeOwnerRecord = (userId: string): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    const record: LocalStorageOwnerRecord = {
      userId,
      verifiedAt: new Date().toISOString(),
      version: 1,
    };

    localStorage.setItem(APP_LOCAL_STORAGE_OWNER_KEY, JSON.stringify(record));
  } catch (error) {
    console.error("Failed to write localStorage owner record:", error);
  }
};

export const clearTrackedAppLocalStorage = (): void => {
  if (!isBrowser()) {
    return;
  }

  const keysToRemove = getLocalStorageKeys().filter(isTrackedLocalStorageKey);

  try {
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to clear tracked localStorage keys:", error);
  }
};

export const syncTrackedLocalStorageOwner = (
  userId: string | null | undefined,
): LocalStorageOwnershipSyncResult => {
  const normalizedUserId = userId?.trim();
  if (!normalizedUserId) {
    return "skipped";
  }

  const ownerRecord = readOwnerRecord();
  if (ownerRecord?.userId === normalizedUserId) {
    return "verified";
  }

  const hasTrackedState = getLocalStorageKeys().some(isTrackedLocalStorageKey);

  if (ownerRecord && ownerRecord.userId !== normalizedUserId) {
    console.warn(
      `[localStorage ownership] State belongs to "${ownerRecord.userId}", but "${normalizedUserId}" signed in. Clearing tracked app state.`,
    );
    clearTrackedAppLocalStorage();
    writeOwnerRecord(normalizedUserId);
    return "cleared-other-user";
  }

  if (!ownerRecord && hasTrackedState) {
    console.warn(
      `[localStorage ownership] Found unowned legacy app state before verifying "${normalizedUserId}". Clearing tracked app state.`,
    );
    clearTrackedAppLocalStorage();
    writeOwnerRecord(normalizedUserId);
    return "cleared-unowned";
  }

  writeOwnerRecord(normalizedUserId);
  return "verified";
};
